import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Link2, Loader2, RefreshCw, ChevronDown, ChevronRight, Unlink,
  ShieldCheck, MapPin, Users, ListChecks,
} from "lucide-react";

function RodeoCard({ m, r }) {
  const [open, setOpen] = useState(false);
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  return (
    <div className={`rounded-xl border ${card}`} data-testid={`cpra-rodeo-${r.code}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 p-4 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className={`font-display font-bold truncate ${title}`}>{r.name}</div>
          <div className="text-xs text-slate-400 truncate">
            {r.entries.map((e) => `${e.event} #${e.position}`).join(" · ")}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          {/* My entries + draw positions */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Your entries & draw positions</div>
            <div className="space-y-1.5">
              {r.entries.map((e, i) => (
                <div key={i} className={`rounded-lg px-3 py-2 text-sm ${m ? "bg-white/5" : "bg-[#FAFBFC] border border-[#EEF0F2]"}`} data-testid={`cpra-entry-${r.code}-${i}`}>
                  <div className={`font-semibold ${title}`}>{e.event} <span className="text-[#C8000F]">· draw #{e.position || "—"}</span></div>
                  <div className="text-xs text-slate-400">
                    {e.performance}{e.tr_partner ? ` · w/ ${e.tr_partner}` : ""}{e.stock ? ` · stock ${e.stock}` : ""}
                  </div>
                </div>
              ))}
            </div>
            {r.conf?.conf_nbr && (
              <div className="text-[11px] text-slate-400 mt-2">Conf #{r.conf.conf_nbr}{r.conf.preference ? ` · preference ${r.conf.preference}` : ""}</div>
            )}
          </div>

          {/* Real entry counts */}
          {r.counts?.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Live entry counts (from CPRA)</div>
              <div className="flex flex-wrap gap-1.5">
                {r.counts.map((c, i) => (
                  <span key={i} data-testid={`cpra-count-${r.code}-${c.event_code}`}
                    className={`text-[11px] px-2 py-1 rounded-md ${m ? "bg-[#C8000F]/15 text-[#F26D77] border border-[#C8000F]/25" : "bg-[#FBF4F5] text-[#9E0008] border border-[#C8000F]/15"}`}
                    title={Object.entries(c.perf || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(", ")}>
                    {c.event_code}: <b>{c.total}</b>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CpraConnect({ m }) {
  const [state, setState] = useState(null); // null=loading
  const [userid, setUserid] = useState("");
  const [password, setPassword] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => api.get("/me/cpra").then((r) => setState(r.data)).catch(() => setState({ connected: false })), []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!userid.trim() || !password) { toast.error("Enter your CPRA member number and password"); return; }
    setConnecting(true);
    try {
      await api.post("/me/cpra/connect", { userid: userid.trim(), password });
      setPassword("");
      toast.success("CPRA account connected");
      await load();
      sync();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setConnecting(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/me/cpra/sync");
      setState(data);
      toast.success(`Synced ${data.rodeos?.length || 0} rodeo${(data.rodeos?.length || 0) === 1 ? "" : "s"} from CPRA`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const disconnect = async () => {
    try { await api.delete("/me/cpra"); toast.success("CPRA account disconnected"); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const inp = m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400";

  return (
    <section data-testid="cpra-connect-section">
      <div className={`text-[11px] font-bold tracking-wider uppercase mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>CPRA Account</div>

      {state === null ? (
        <div className={`rounded-xl border p-6 flex justify-center ${card}`}><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
      ) : !state.connected ? (
        <div className={`rounded-xl border p-5 ${card}`} data-testid="cpra-connect-card">
          <div className={`flex items-center gap-2 font-display font-bold mb-1 ${title}`}><Link2 className="w-4 h-4 text-[#C8000F]" /> Connect your CPRA account</div>
          <p className="text-sm text-slate-400 mb-3">Pull your real entries, draw positions and live entry counts straight from cpralogin.com. Your login is stored encrypted and only used when you tap Sync.</p>
          <div className="space-y-2">
            <input value={userid} onChange={(e) => setUserid(e.target.value)} placeholder="CPRA member number (e.g. 325146)" data-testid="cpra-userid-input"
              className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inp}`} />
            <input value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && connect()} type="password" placeholder="CPRA password" data-testid="cpra-password-input"
              className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inp}`} />
            <button onClick={connect} disabled={connecting} data-testid="cpra-connect-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-50">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Connect
            </button>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-3"><ShieldCheck className="w-3.5 h-3.5 text-[#3B6B1E]" /> Encrypted at rest · never shared · disconnect anytime</div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className={`rounded-xl border p-5 ${card}`} data-testid="cpra-connected-card">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className={`flex items-center gap-2 font-display font-bold ${title}`}><ShieldCheck className="w-4 h-4 text-[#3B6B1E]" /> CPRA Connected</div>
              <button onClick={disconnect} data-testid="cpra-disconnect-btn" className="text-xs font-semibold text-slate-400 hover:text-[#C8000F] flex items-center gap-1"><Unlink className="w-3.5 h-3.5" /> Disconnect</button>
            </div>
            <p className="text-sm text-slate-400 mb-3">
              {state.member?.name ? <><b className={title}>{state.member.name}</b> · #{state.userid}</> : <>Member #{state.userid}</>}
              {state.synced_at ? <> · last synced {new Date(state.synced_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</> : <> · not synced yet</>}
            </p>
            <button onClick={sync} disabled={syncing} data-testid="cpra-sync-btn"
              className="w-full flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-50">
              {syncing ? <><Loader2 className="w-4 h-4 animate-spin" /> Syncing…</> : <><RefreshCw className="w-4 h-4" /> Sync from CPRA</>}
            </button>
          </div>

          {(state.rodeos?.length > 0) && (
            <div>
              <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>
                <MapPin className="w-3.5 h-3.5" /> Your entries · {state.rodeos.length} rodeo{state.rodeos.length === 1 ? "" : "s"}
              </div>
              <div className="space-y-2">
                {state.rodeos.map((r) => <RodeoCard key={r.code} m={m} r={r} />)}
              </div>
            </div>
          )}

          {(state.trades?.length > 0) && (
            <div className={`rounded-xl border p-5 ${card}`} data-testid="cpra-trades-card">
              <div className={`flex items-center gap-2 font-display font-bold mb-2 ${title}`}><Users className="w-4 h-4 text-[#E68A2E]" /> Trade List ({state.trades.length})</div>
              <div className="space-y-1.5">
                {state.trades.map((t, i) => (
                  <div key={i} className={`text-sm rounded-lg px-3 py-2 ${m ? "bg-white/5 text-slate-200" : "bg-[#FAFBFC] border border-[#EEF0F2] text-[#14181F]"}`} data-testid={`cpra-trade-${i}`}>
                    {t.cells ? t.cells.join(" · ") : Object.entries(t).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                  </div>
                ))}
              </div>
            </div>
          )}

          {state.synced_at && (!state.rodeos || state.rodeos.length === 0) && (
            <div className={`rounded-xl border border-dashed p-6 text-center text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`}>
              <ListChecks className="w-5 h-5 mx-auto mb-2 opacity-60" />
              No current entries found on your CPRA account.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
